// client/src/components/ScrollToTop.tsx — OTT 2026
//
// Segnalazione di Giuseppe: «quando clicco su "i miei eventi" la pagina che si
// apre parte dalla fine! Ogni pulsante cliccato deve aprire la propria pagina
// dall'inizio».
//
// PERCHÉ NON BASTAVA window.scrollTo: in RideConnect lo scroll NON vive sempre
// sulla finestra. A seconda del guscio (app, web, area dealer, admin) il
// contenitore che scorre è:
//   - `#root` (app/WebView, vedi global.css: height:100dvh + overflow-y:auto)
//   - il `<div data-scroll-container>` del Layout (shell desktop/dealer)
//   - il `<main>` di Admin/Partner
//   - qualsiasi altro elemento con overflow (liste interne)
//
// Qui l'azzeramento è DEFINITIVO e indipendente dai selettori: intercettiamo
// l'ultimo elemento che ha scrollato (listener in fase di capture, installato
// una volta sola) e lo azzeriamo al cambio rotta, insieme ai contenitori noti e
// alla finestra. Un secondo passaggio (frame + micro-task) copre le pagine lazy
// che montano contenuto dopo il primo paint.
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/** Ultimo elemento che ha generato un evento di scroll (qualsiasi container). */
let lastScrolledElement: HTMLElement | null = null;
let listenerInstalled = false;

function installScrollTracker(): void {
  if (listenerInstalled || typeof document === "undefined") return;
  listenerInstalled = true;
  // capture = true: gli eventi `scroll` non fanno bubbling, ma in capture
  // arrivano anche quelli dei contenitori interni.
  document.addEventListener(
    "scroll",
    (event) => {
      const target = event.target;
      if (target instanceof HTMLElement) lastScrolledElement = target;
    },
    true,
  );
}

function resetElement(el: HTMLElement | null | undefined): void {
  if (!el) return;
  try {
    if (el.scrollTop !== 0) el.scrollTop = 0;
    if (el.scrollLeft !== 0) el.scrollLeft = 0;
  } catch {
    /* elemento non più nel DOM: niente da fare */
  }
}

/** Azzera TUTTI i contenitori di scroll che possono esistere in pagina. */
export function resetPageScroll(): void {
  if (typeof document === "undefined") return;

  installScrollTracker();
  resetElement(lastScrolledElement);
  lastScrolledElement = null;

  resetElement(document.getElementById("root"));
  resetElement(document.documentElement);
  resetElement(document.body);

  document
    .querySelectorAll<HTMLElement>("[data-scroll-container]")
    .forEach(resetElement);

  // Gusci admin/partner e pagine con il proprio <main>
  document.querySelectorAll<HTMLElement>("main").forEach(resetElement);

  if (typeof window !== "undefined" && typeof window.scrollTo === "function") {
    try {
      window.scrollTo(0, 0);
    } catch {
      /* Safari in alcuni contesti: ignoriamo */
    }
  }
}

export default function ScrollToTop() {
  const { pathname, search, hash, key } = useLocation();
  useEffect(() => {
    installScrollTracker();
    resetPageScroll();

    // Pagine lazy: il contenuto (e quindi l'altezza) cambia dopo il primo
    // paint. Un secondo e terzo azzeramento coprono il caso senza sfarfallii.
    let raf: number | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;

    if (typeof requestAnimationFrame === "function") {
      raf = requestAnimationFrame(() => {
        resetPageScroll();
        timer = setTimeout(resetPageScroll, 80);
      });
    } else {
      timer = setTimeout(resetPageScroll, 0);
    }

    return () => {
      if (raf !== null) cancelAnimationFrame(raf);
      if (timer !== null) clearTimeout(timer);
    };
    // `key` cambia a OGNI navigazione: copre anche il tap sulla stessa voce di
    // menu (es. "I miei Eventi" mentre sei già lì), che deve comunque ripartire
    // dall'inizio.
  }, [pathname, search, hash, key]);

  return null;
}
